import routes from '@config/routes';
import { useBuildPackageBundle } from '@hooks/use-build-package-bundle';
import { serverFetcher } from '@instance';
import { BaseProperty } from '@interfaces/expedia/property';
import { useSearchHotelStore } from '@store/search-hotel-store';
import { useEffect, useMemo } from 'react';
import { useBuildPackagesStore } from 'src/stores/build-package-store';
import useSWR from 'swr';
import { toHotelDistanceMiles } from '../hotel-distance';
import {
  getLowestHotelPrice,
  isMapCoordinate,
  milesBetween,
} from '../hotel-presentation';
import { filterHotels, sortHotels } from '../utils/filter-hotels';

const DEFAULT_MAX_PRICE = 6500;

function hotelCoordinates(
  property: object
): { latitude: number; longitude: number } | null {
  if (!('latitude' in property) || !('longitude' in property)) return null;
  const record = property as Record<string, unknown>;
  const latitude = record.latitude;
  const longitude = record.longitude;
  if (typeof latitude !== 'number' || typeof longitude !== 'number') {
    return null;
  }
  return { latitude, longitude };
}

export function useGetHotels() {
  const distance = useSearchHotelStore((state) =>
    toHotelDistanceMiles(state.hotelFilters.distance)
  );

  const selectedTicketPrice = useBuildPackagesStore(
    (state) => state.selectedTicketPrice
  );
  const sortBy = useSearchHotelStore((state) => state.hotelFilters.sortBy);

  const { isBundle } = useBuildPackageBundle();

  const query = useSearchHotelStore((state) => state.hotelFilters.query);
  const ratings = useSearchHotelStore((state) => state.hotelFilters.rating);
  const priceRange = useSearchHotelStore(
    (state) => state.hotelFilters.pricePerTraveler
  );
  const setHotelFilter = useSearchHotelStore((state) => state.setHotelFilters);

  const featureHotel = useSearchHotelStore(
    (state) => state.hotelFilters.featureHotel
  );
  const startDate = useBuildPackagesStore((state) => state.hotelStartDate);
  const endDate = useBuildPackagesStore((state) => state.hotelEndDate);
  const location = useBuildPackagesStore((state) => state.location);
  const roomConfigurations = useBuildPackagesStore(
    (state) => state.roomConfigurations
  );
  const searchOrigin = useMemo(() => {
    const latitude = location?.latitude;
    const longitude = location?.longitude;
    if (latitude == null || longitude == null) return null;
    const lat = Number(latitude);
    const lng = Number(longitude);
    return isMapCoordinate(lat, lng) ? { latitude: lat, longitude: lng } : null;
  }, [location]);
  const base = useMemo(
    () => ({
      startDate,
      endDate,
      location,
      roomConfigurations,
    }),
    [endDate, location, roomConfigurations, startDate]
  );
  const canSearchHotels = Boolean(
    base.startDate &&
    base.endDate &&
    base.location &&
    searchOrigin &&
    base.roomConfigurations.length > 0
  );

  const { data, isLoading, isValidating, error, mutate } = useSWR<{
    error: [];
    properties: BaseProperty[];
  }>(
    canSearchHotels
      ? [
          routes.propertySearch,
          base.endDate,
          base.startDate,
          base.location,
          base.roomConfigurations,
          distance,
          'distance-low-high',
          Boolean(isBundle),
        ]
      : null,
    (url) =>
      serverFetcher(url[0], {
        body: {
          base,
          filters: {
            distance,
            sortBy: 'distance-low-high',
          },
          options: {
            // A.S:Property not currently used when filtering properties
            requiredCurrency: 'USD',
            maxResult: 100,
            isBundle: Boolean(isBundle),
          },
        },
      }),
    {
      dedupingInterval: 60_000,
      refreshInterval: 120_000,
      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const maxHotelPrice = useMemo(() => {
    const properties = data?.properties;
    if (properties?.length === 0) return DEFAULT_MAX_PRICE;
    let mp =
      properties?.reduce((max, hot) => {
        if (hot.price > max) max = hot.price;
        return max;
      }, Number.MIN_SAFE_INTEGER) ?? 0;

    if (mp === Number.MIN_SAFE_INTEGER) mp = 0;

    const addOnPrice = isBundle ? selectedTicketPrice : 0;
    if (mp + addOnPrice > DEFAULT_MAX_PRICE) {
      return mp + addOnPrice;
    }

    return DEFAULT_MAX_PRICE;
  }, [data, isBundle, selectedTicketPrice]);

  useEffect(() => {
    if (
      maxHotelPrice > DEFAULT_MAX_PRICE &&
      !useSearchHotelStore.getState().hasPricePerTravelerChanged
    ) {
      setHotelFilter({ pricePerTraveler: [0, maxHotelPrice] });
      useSearchHotelStore.setState({
        hasPricePerTravelerChanged: false,
      });
    }
  }, [maxHotelPrice, DEFAULT_MAX_PRICE]);

  const listFiltered = useMemo(() => {
    if (!data?.properties?.length) return [];
    const withSelectedDistance = data.properties.map((property) => {
      const coords = hotelCoordinates(property);
      if (
        !searchOrigin ||
        !coords ||
        !isMapCoordinate(coords.latitude, coords.longitude)
      ) {
        return property;
      }
      return {
        ...property,
        distance: milesBetween(coords, searchOrigin),
      };
    });

    return sortHotels(
      filterHotels(withSelectedDistance, {
        query,
        priceRange,
        ratings,
        amenities: featureHotel,
        isBundle: Boolean(isBundle),
        selectedTicketPrice,
        maxDistance: distance,
      }),
      sortBy
    );
  }, [
    data?.properties,
    query,
    ratings,
    priceRange,
    featureHotel,
    isBundle,
    sortBy,
    selectedTicketPrice,
    searchOrigin,
    distance,
  ]);

  const lowestHotelPrice = useMemo(
    () => getLowestHotelPrice(data?.properties ?? []),
    [data?.properties]
  );

  return {
    data: listFiltered,
    canSearchHotels,
    hasCurrentData: canSearchHotels && data !== undefined,
    isLoading,
    isValidating,
    maxHotelPrice,
    lowestHotelPrice,
    error,
    mutate,
  };
}
